import {
  Flex,
  Link,
  Text,
} from '@chakra-ui/react';
import {
  usePavementRehabilitation,
  usePavementResurfacing,
  useUnsealedRoadMetalling,
} from '@src/modules/performance/context/reports-api-hooks';
import LineBarChart from '@src/modules/performance/components/charts/LineBarChart';
import { CHART_COLOURS } from '@src/modules/performance/shared/constants';
import ReportSection from './ReportSection';

const informationSource = {
  title: 'Road Maintenance Activities (Roads)',
  content: [
    {
      title: 'Pavement rehabilitation (lane km)',
      description:
  <>
    <Text fontSize="inherit" mb={2}>A comparison between the planned/forecast length of pavement rehabilitation and the actual reported lengths achieved.</Text>
    <Text fontSize="inherit" mb={2}>Note: The quantity recorded in TIO in lane km is reported. In the event an RCA has populated the m2 quantity only this will report as null on the chart. Forecast values are based on the requested levels of funding and Achieved values are based on the approved levels of funding.</Text>
  </>,
      source: null,
    },
    {
      title: 'Pavement resurfacing (lane km)',
      description:
  <>
    <Text fontSize="inherit" mb={2}>A comparison between the planned/forecast length of resurfacing renewals and the actual reported lengths achieved.</Text>
    <Text fontSize="inherit" mb={2}>Note: The quantity recorded in TIO in lane km is reported. In the event an RCA has populated the m2 quantity only this will report as null on the chart.</Text>
  </>,
      source: null,
    },
    {
      title: 'Unsealed Road Remetalling (km)',
      description:
  <Text fontSize="inherit" mb={2}>A comparison between the planned/forecast km length of unsealed road remetalling and the actual reported km lengths achieved.</Text>,
      source:
  <Text fontSize="inherit" as="span">
    Forecast and Achieved are sourced from
    {' '}
    <Link href="https://www.nzta.govt.nz/roads-and-rail/highways-information-portal/processes/transport-investment-online/" isExternal>NZTA Transport Investment Online.</Link>
  </Text>,
    },
  ],
};

function RoadMaintenanceDeliveryActivitiesRoads() {
  const { data: pavementRehabilitation, isLoading: isLoadingRehab } = usePavementRehabilitation();
  const { data: pavementResurfacing, isLoading: isLoadingResurfacing } = usePavementResurfacing();
  const { data: unsealedRoadMetalling, isLoading: isLoadingUnsealed } = useUnsealedRoadMetalling();
  const isLoading = isLoadingRehab || isLoadingResurfacing || isLoadingUnsealed;

  // Pavement Rehab Chart
  const pavementRehabilitationCategories = [...new Set(pavementRehabilitation?.map((item) => item.Year))].sort((a, b) => a - b);
  const pavementRehabilitationData = [{
    name: 'Variance %',
    type: 'line',
    data: pavementRehabilitationCategories.map((year) => ({
      x: year,
      y: pavementRehabilitation?.find((item) => item.Year === year)?.Variance,
    })),
    color: CHART_COLOURS.grey,
  },
  {
    name: 'Forecast',
    type: 'bar',
    data: pavementRehabilitationCategories.map((year) => ({
      x: year,
      y: pavementRehabilitation?.find((item) => item.Year === year)?.Planned || null,
    })),
    color: CHART_COLOURS.blue,
  },
  {
    name: 'Achieved',
    type: 'bar',
    data: pavementRehabilitationCategories.map((year) => ({
      x: year,
      y: pavementRehabilitation?.find((item) => item.Year === year)?.Actual || null,
    })),
    color: CHART_COLOURS.red,
  },
  ];

  // Pavement Resurfacing Chart
  const pavementResurfacingCategories = [...new Set(pavementResurfacing?.map((item) => item.Year))].sort((a, b) => a - b);
  const pavementResurfacingData = [{
    name: 'Variance %',
    type: 'line',
    data: pavementResurfacingCategories.map((year) => ({
      x: year,
      y: pavementResurfacing?.find((item) => item.Year === year)?.Variance,
    })),
    color: CHART_COLOURS.grey,
  },
  {
    name: 'Forecast',
    type: 'bar',
    data: pavementResurfacingCategories.map((year) => ({
      x: year,
      y: pavementResurfacing?.find((item) => item.Year === year)?.Planned || null,
    })),
    color: CHART_COLOURS.blue,
  },
  {
    name: 'Achieved',
    type: 'bar',
    data: pavementResurfacingCategories.map((year) => ({
      x: year,
      y: pavementResurfacing?.find((item) => item.Year === year)?.Actual || null,
    })),
    color: CHART_COLOURS.red,
  },
  ];

  // Unsealed Road Metalling Chart
  const unsealedRoadMetallingCategories = [...new Set(unsealedRoadMetalling?.map((item) => item.Year))].sort((a, b) => a - b);
  const unsealedRoadMetallingData = [{
    name: 'Variance %',
    type: 'line',
    data: unsealedRoadMetallingCategories.map((year) => ({
      x: year,
      y: unsealedRoadMetalling?.find((item) => item.Year === year)?.Variance,
    })),
    color: CHART_COLOURS.grey,
  },
  {
    name: 'Forecast',
    type: 'bar',
    data: unsealedRoadMetallingCategories.map((year) => ({
      x: year,
      y: unsealedRoadMetalling?.find((item) => item.Year === year)?.Planned || null,
    })),
    color: CHART_COLOURS.blue,
  },
  {
    name: 'Achieved',
    type: 'bar',
    data: unsealedRoadMetallingCategories.map((year) => ({
      x: year,
      y: unsealedRoadMetalling?.find((item) => item.Year === year)?.Actual || null,
    })),
    color: CHART_COLOURS.red,
  },
  ];

  // Remove Variance series if Forecast or Actual is null for all years
  if (pavementRehabilitation && pavementRehabilitation.every((item) => item.Planned === null || item.Actual === null)) {
    pavementRehabilitationData.shift();
  }
  if (pavementResurfacing && pavementResurfacing.every((item) => item.Planned === null || item.Actual === null)) {
    pavementResurfacingData.shift();
  }
  if (unsealedRoadMetalling && unsealedRoadMetalling.every((item) => item.Planned === null || item.Actual === null)) {
    unsealedRoadMetallingData.shift();
  }

  return (
    <ReportSection
      title="Road Maintenance Activities (Roads)"
      isLoading={isLoading}
      lockYear={pavementRehabilitation?.[0]?.LockYear}
      informationSource={informationSource}
      links={[
        {
          label: 'ONF Sealed Road Pavement Rehabilitation & Resurfacing',
          url: '/onf/transport-outcomes/ep4',
          isExternal: false,
          userRoles: null,
        },
      ]}
    >
      <Flex flexDirection="row">
        <LineBarChart
          title="Pavement rehabilitation (lane km)"
          categories={pavementRehabilitationCategories}
          series={pavementRehabilitationData}
          width="280"
          height="280"
          leftAxisLabel="Lane km"
          rightAxisLabel="Variance %"
          lineSeriesName="Variance %"
          barSeriesName="Forecast"
          barSeriesFormatter={(y) => (typeof y !== 'undefined' && y !== null ? `${y.toFixed(0)}km` : '')}
          lineSeriesFormatter={(y) => (typeof y !== 'undefined' ? `${y.toFixed(0)}%` : '')}
        />
        <LineBarChart
          title="Pavement resurfacing (lane km)"
          categories={pavementResurfacingCategories}
          series={pavementResurfacingData}
          width="280"
          height="280"
          leftAxisLabel="Lane km"
          rightAxisLabel="Variance %"
          lineSeriesName="Variance %"
          barSeriesName="Forecast"
          barSeriesFormatter={(y) => (typeof y !== 'undefined' && y !== null ? `${y.toFixed(0)}km` : '')}
          lineSeriesFormatter={(y) => (typeof y !== 'undefined' ? `${y.toFixed(0)}%` : '')}
        />
        <LineBarChart
          title="Unsealed road remetalling (km)"
          categories={unsealedRoadMetallingCategories}
          series={unsealedRoadMetallingData}
          width="280"
          height="280"
          leftAxisLabel="km"
          rightAxisLabel="Variance %"
          lineSeriesName="Variance %"
          barSeriesName="Forecast"
          barSeriesFormatter={(y) => (typeof y !== 'undefined' && y !== null ? `${y.toFixed(0)}km` : '')}
          lineSeriesFormatter={(y) => (typeof y !== 'undefined' ? `${y.toFixed(0)}%` : '')}
        />
      </Flex>
    </ReportSection>
  );
}

export default RoadMaintenanceDeliveryActivitiesRoads;
